/**
 * Units of ingredient lines (PLAN.md §6, §8): canonical keys, their dimension and factor to the
 * dimension's base (g for mass, ml for volume), and the words that name them in German and
 * English recipes. Pure data + lookups; used by the parser, scaling and shopping aggregation.
 */
export type UnitDimension = 'mass' | 'volume' | 'count' | 'length';

export interface UnitDef {
  dimension: UnitDimension;
  /** Factor to grams (mass) or millilitres (volume); 1 for count units. */
  factor: number;
  /** Display [singular, plural] per language. */
  de: readonly [string, string];
  en: readonly [string, string];
  /** Spellings found in recipes (matched case-insensitively, a trailing dot optional). */
  aliases: readonly string[];
}

export const UNITS = {
  mg: {
    dimension: 'mass',
    factor: 0.001,
    de: ['mg', 'mg'],
    en: ['mg', 'mg'],
    aliases: ['mg', 'milligramm', 'milligram', 'milligrams'],
  },
  g: {
    dimension: 'mass',
    factor: 1,
    de: ['g', 'g'],
    en: ['g', 'g'],
    aliases: ['g', 'gr', 'grm', 'gramm', 'gram', 'grams', 'gramme', 'grammes'],
  },
  kg: {
    dimension: 'mass',
    factor: 1000,
    de: ['kg', 'kg'],
    en: ['kg', 'kg'],
    aliases: ['kg', 'kilo', 'kilos', 'kilogramm', 'kilogram', 'kilograms'],
  },
  oz: {
    dimension: 'mass',
    factor: 28.35,
    de: ['oz', 'oz'],
    en: ['oz', 'oz'],
    aliases: ['oz', 'ounce', 'ounces', 'unze', 'unzen'],
  },
  lb: {
    dimension: 'mass',
    factor: 453.6,
    de: ['lb', 'lb'],
    en: ['lb', 'lb'],
    aliases: ['lb', 'lbs', 'pound', 'pounds', 'pfund'],
  },
  ml: {
    dimension: 'volume',
    factor: 1,
    de: ['ml', 'ml'],
    en: ['ml', 'ml'],
    aliases: ['ml', 'milliliter', 'millilitre', 'milliliters', 'millilitres'],
  },
  cl: {
    dimension: 'volume',
    factor: 10,
    de: ['cl', 'cl'],
    en: ['cl', 'cl'],
    aliases: ['cl', 'zentiliter', 'centiliter', 'centilitre'],
  },
  dl: {
    dimension: 'volume',
    factor: 100,
    de: ['dl', 'dl'],
    en: ['dl', 'dl'],
    aliases: ['dl', 'deziliter', 'deciliter', 'decilitre'],
  },
  l: {
    dimension: 'volume',
    factor: 1000,
    de: ['l', 'l'],
    en: ['l', 'l'],
    aliases: ['l', 'ltr', 'liter', 'litre', 'liters', 'litres'],
  },
  tsp: {
    dimension: 'volume',
    factor: 5,
    de: ['TL', 'TL'],
    en: ['tsp', 'tsp'],
    aliases: ['tl', 'teel', 'teelöffel', 'tsp', 'tsps', 'teaspoon', 'teaspoons'],
  },
  tbsp: {
    dimension: 'volume',
    factor: 15,
    de: ['EL', 'EL'],
    en: ['tbsp', 'tbsp'],
    aliases: [
      'el',
      'essl',
      'esslöffel',
      'eßlöffel',
      'tbsp',
      'tbsps',
      'tbs',
      'tbl',
      'tablespoon',
      'tablespoons',
    ],
  },
  cup: {
    dimension: 'volume',
    factor: 240,
    de: ['Tasse', 'Tassen'],
    en: ['cup', 'cups'],
    aliases: ['cup', 'cups', 'c', 'tasse', 'tassen'],
  },
  fl_oz: {
    dimension: 'volume',
    factor: 29.57,
    de: ['fl oz', 'fl oz'],
    en: ['fl oz', 'fl oz'],
    aliases: ['fl oz', 'fl. oz', 'fluid ounce', 'fluid ounces'],
  },
  pinch: {
    dimension: 'count',
    factor: 1,
    de: ['Prise', 'Prisen'],
    en: ['pinch', 'pinches'],
    aliases: ['prise', 'prisen', 'pinch', 'pinches', 'pr'],
  },
  knifetip: {
    dimension: 'count',
    factor: 1,
    de: ['Msp.', 'Msp.'],
    en: ['knife tip', 'knife tips'],
    aliases: ['msp', 'messerspitze', 'messerspitzen'],
  },
  dash: {
    dimension: 'count',
    factor: 1,
    de: ['Schuss', 'Schuss'],
    en: ['dash', 'dashes'],
    aliases: ['schuss', 'spritzer', 'dash', 'dashes', 'splash', 'splashes'],
  },
  drop: {
    dimension: 'count',
    factor: 1,
    de: ['Tropfen', 'Tropfen'],
    en: ['drop', 'drops'],
    aliases: ['tropfen', 'drop', 'drops'],
  },
  piece: {
    dimension: 'count',
    factor: 1,
    de: ['Stück', 'Stück'],
    en: ['piece', 'pieces'],
    aliases: ['stück', 'stk', 'st', 'stck', 'piece', 'pieces', 'pc', 'pcs'],
  },
  bunch: {
    dimension: 'count',
    factor: 1,
    de: ['Bund', 'Bund'],
    en: ['bunch', 'bunches'],
    aliases: ['bund', 'bd', 'bunch', 'bunches'],
  },
  can: {
    dimension: 'count',
    factor: 1,
    de: ['Dose', 'Dosen'],
    en: ['can', 'cans'],
    aliases: ['dose', 'dosen', 'ds', 'can', 'cans', 'tin', 'tins'],
  },
  jar: {
    dimension: 'count',
    factor: 1,
    de: ['Glas', 'Gläser'],
    en: ['jar', 'jars'],
    aliases: ['glas', 'gläser', 'gl', 'jar', 'jars'],
  },
  pack: {
    dimension: 'count',
    factor: 1,
    de: ['Packung', 'Packungen'],
    en: ['pack', 'packs'],
    aliases: [
      'packung',
      'packungen',
      'pck',
      'pkg',
      'pk',
      'päckchen',
      'pack',
      'packs',
      'package',
      'packages',
      'packet',
      'packets',
    ],
  },
  pot: {
    dimension: 'count',
    factor: 1,
    de: ['Becher', 'Becher'],
    en: ['tub', 'tubs'],
    aliases: ['becher', 'tub', 'tubs', 'pot', 'pots'],
  },
  bottle: {
    dimension: 'count',
    factor: 1,
    de: ['Flasche', 'Flaschen'],
    en: ['bottle', 'bottles'],
    aliases: ['flasche', 'flaschen', 'fl', 'bottle', 'bottles'],
  },
  bag: {
    dimension: 'count',
    factor: 1,
    de: ['Beutel', 'Beutel'],
    en: ['bag', 'bags'],
    aliases: ['beutel', 'btl', 'tüte', 'tüten', 'bag', 'bags', 'sachet', 'sachets'],
  },
  clove: {
    dimension: 'count',
    factor: 1,
    de: ['Zehe', 'Zehen'],
    en: ['clove', 'cloves'],
    aliases: ['zehe', 'zehen', 'clove', 'cloves'],
  },
  slice: {
    dimension: 'count',
    factor: 1,
    de: ['Scheibe', 'Scheiben'],
    en: ['slice', 'slices'],
    aliases: ['scheibe', 'scheiben', 'slice', 'slices'],
  },
  handful: {
    dimension: 'count',
    factor: 1,
    de: ['Handvoll', 'Handvoll'],
    en: ['handful', 'handfuls'],
    aliases: ['handvoll', 'hand voll', 'handful', 'handfuls'],
  },
  sprig: {
    dimension: 'count',
    factor: 1,
    de: ['Zweig', 'Zweige'],
    en: ['sprig', 'sprigs'],
    aliases: ['zweig', 'zweige', 'zweiglein', 'sprig', 'sprigs'],
  },
  stalk: {
    dimension: 'count',
    factor: 1,
    de: ['Stange', 'Stangen'],
    en: ['stalk', 'stalks'],
    aliases: [
      'stange',
      'stangen',
      'stängel',
      'stiel',
      'stiele',
      'stalk',
      'stalks',
      'stick',
      'sticks',
    ],
  },
  leaf: {
    dimension: 'count',
    factor: 1,
    de: ['Blatt', 'Blätter'],
    en: ['leaf', 'leaves'],
    aliases: ['blatt', 'blätter', 'leaf', 'leaves', 'sheet', 'sheets'],
  },
  head: {
    dimension: 'count',
    factor: 1,
    de: ['Kopf', 'Köpfe'],
    en: ['head', 'heads'],
    aliases: ['kopf', 'köpfe', 'knolle', 'knollen', 'head', 'heads', 'bulb', 'bulbs'],
  },
  cube: {
    dimension: 'count',
    factor: 1,
    de: ['Würfel', 'Würfel'],
    en: ['cube', 'cubes'],
    aliases: ['würfel', 'cube', 'cubes'],
  },
  cm: {
    dimension: 'length',
    factor: 1,
    de: ['cm', 'cm'],
    en: ['cm', 'cm'],
    aliases: ['cm', 'zentimeter', 'centimeter', 'centimetre', 'centimetres'],
  },
} as const satisfies Record<string, UnitDef>;

export type Unit = keyof typeof UNITS;
export const UNIT_KEYS = Object.keys(UNITS) as Unit[];

export const isUnit = (value: string | null | undefined): value is Unit =>
  typeof value === 'string' && Object.hasOwn(UNITS, value);

const aliasIndex = new Map<string, Unit>();
for (const key of UNIT_KEYS) {
  for (const alias of UNITS[key].aliases) aliasIndex.set(alias, key);
}

/** All aliases, longest first (so "fl oz" wins over "fl"). */
export const UNIT_ALIASES: readonly string[] = [...aliasIndex.keys()].sort(
  (a, b) => b.length - a.length,
);

/** The unit a word names ("EL", "Esslöffel", "tbsp." → `tbsp`), or null. */
export function unitFromWord(word: string): Unit | null {
  return aliasIndex.get(word.toLowerCase().replace(/\.$/, '')) ?? null;
}

export function unitDimension(unit: Unit | null): UnitDimension {
  return unit ? UNITS[unit].dimension : 'count';
}

/** Display label for a unit in a language, singular or plural. */
export function unitLabel(unit: Unit, lang: 'de' | 'en', quantity = 1): string {
  const [one, many] = UNITS[unit][lang];
  return quantity > 0 && quantity <= 1 ? one : many;
}
